import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Star, Plus, Maximize2, Eye, ExternalLink } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { swatchFor } from '../lib/colors';
import NotifyMe from './NotifyMe';
import ImageLightbox from './ImageLightbox';
import ShadeCompare from './ShadeCompare';
import QuickView from './QuickView';
import Tilt from './motion/Tilt';
import { activeSale, withSale } from '../lib/sale';

const ProductCard = ({ product, index = 0, comingSoonClickable = false }) => {
  const { addItem } = useCart();
  const colors = product.colors || [];
  const sale = activeSale(product);
  const comingSoon = product.coming_soon;
  const colorImages = product.color_images || {};
  const [hoverImg, setHoverImg] = useState(null);
  const [hoverColor, setHoverColor] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [showAllSwatches, setShowAllSwatches] = useState(false);
  const justRestocked = !!product.restocked_at && (Date.now() - new Date(product.restocked_at).getTime()) < 48 * 3600 * 1000;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const rawVideo = product.image_first ? null : (product.video || (product.videos && product.videos[0]) || null);
  const texturePeek = product.category === 'bundles' ? rawVideo : null;
  const heroVideo = texturePeek ? null : rawVideo;
  const [peek, setPeek] = useState(false);
  const peekRef = useRef(null);
  const fitClass = product.image_fit === 'contain' ? 'object-contain' : 'object-cover';
  const mediaAspect = product.category === 'accessories' && product.image_fit === 'contain' ? 'aspect-square' : 'aspect-[3/4]';
  const maxSwatches = Object.keys(colorImages).length ? 6 : 5;
  const secondImg = !heroVideo && !texturePeek && product.gallery && product.gallery.length > 1 ? product.gallery[1] : null;
  const baseImg = (selectedColor && colorImages[selectedColor]) || product.image;
  const enlargeImages = (() => {
    const g = product.gallery && product.gallery.length ? product.gallery : [product.image];
    return hoverImg ? [hoverImg, ...g.filter((s) => s !== hoverImg)] : g;
  })();

  const openLightbox = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLightboxOpen(true);
  };

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.external_url) {
      window.open(product.external_url, '_blank', 'noopener');
      return;
    }
    const len = product.lengths?.[0];
    const price = product.price_by_length?.[len] ?? product.price;
    const r = e.currentTarget.getBoundingClientRect();
    addItem(product, {
      length: len,
      color: selectedColor || product.colors?.[0],
      price,
      confettiAt: { x: (r.left + r.width / 2) / window.innerWidth, y: r.top / window.innerHeight },
    });
  };

  if (comingSoon) {
    const media = (
      <div className="relative overflow-hidden bg-[#ece5d8] aspect-[3/4]">
        <img src={product.image} alt={product.name} loading="lazy"
          className="w-full h-full object-cover img-zoom grayscale-[15%]" />
        <div className="absolute inset-0 bg-[#0b0b0b]/35" />
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overline text-[0.65rem] text-[#e7d3a1] border border-[#c6a15b]/60 bg-[#0b0b0b]/70 px-4 py-2">Coming Soon</span>
      </div>
    );
    const info = (
      <div className="pt-4 text-center">
        <h3 className="font-display text-lg text-[#1a1a1a] leading-snug px-2">{product.name}</h3>
        <p className="font-body text-sm text-[#6b6459] mt-1 tracking-wide">Coming Soon</p>
      </div>
    );
    return (
      <div className="group block" data-testid={`coming-soon-card-${product.id}`}>
        {comingSoonClickable ? (
          <Link to={`/product/${product.id}`} className="block">{media}{info}</Link>
        ) : (
          <>{media}{info}</>
        )}
        <div className="text-center mt-3">
          <NotifyMe productId={product.id} variant="card" />
        </div>
      </div>
    );
  }

  return (
    <Tilt>
    <Link to={`/product/${product.id}`} className="group block card-lift">
      <div className={`relative overflow-hidden bg-[#ece5d8] ${mediaAspect}`}
        onMouseEnter={() => { if (texturePeek) { setPeek(true); peekRef.current?.play?.()?.catch?.(() => {}); } }}
        onMouseLeave={() => { setHoverImg(null); setHoverColor(null); if (texturePeek) { setPeek(false); peekRef.current?.pause?.(); } }}>
        {heroVideo ? (
          <video src={heroVideo} poster={product.image} autoPlay muted loop playsInline preload="metadata"
            className={`w-full h-full ${fitClass}`} data-testid={`card-video-${product.id}`} />
        ) : (
          <img src={baseImg} alt={product.name} loading="lazy"
            className={`w-full h-full ${fitClass} img-zoom transition-opacity duration-500`} />
        )}
        {secondImg && !selectedColor && (
          <img src={secondImg} alt="" loading="lazy" aria-hidden="true"
            className={`absolute inset-0 w-full h-full ${fitClass} opacity-0 group-hover:opacity-100 transition-opacity duration-700`} />
        )}
        {texturePeek && (
          <video ref={peekRef} src={texturePeek} muted loop playsInline preload="none"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 pointer-events-none ${peek ? 'opacity-100' : 'opacity-0'}`}
            data-testid={`card-peek-${product.id}`} />
        )}
        {hoverImg && (
          <img src={hoverImg} alt="" className={`absolute inset-0 w-full h-full ${fitClass}`} />
        )}
        <div className="absolute top-3 left-3 z-10 flex flex-col items-start gap-1.5 pointer-events-none">
          {justRestocked && (
            <span className="restock-ribbon overline text-[0.58rem] px-3 py-1.5" data-testid={`restock-ribbon-${product.id}`}>Just Restocked</span>
          )}
          {product.bestseller && (
            <span className="bg-[#0b0b0b] text-[#e7d3a1] overline text-[0.58rem] px-3 py-1.5">Bestseller</span>
          )}
          {sale && (
            <span className="bg-[#c6a15b] text-[#0b0b0b] overline text-[0.58rem] px-3 py-1.5" data-testid={`sale-badge-${product.id}`}>{sale.percent}% Off</span>
          )}
          {texturePeek && peek && !hoverImg && (
            <span className="bg-[#0b0b0b]/85 text-[#e7d3a1] overline text-[0.55rem] px-2.5 py-1">Texture Peek</span>
          )}
        </div>
        {hoverImg && (
          <span className="absolute bottom-16 left-3 z-10 bg-[#0b0b0b]/85 text-[#e7d3a1] overline text-[0.55rem] px-2.5 py-1">Preview</span>
        )}
        {!hoverImg && (hoverColor || selectedColor) && (
          <span className="absolute bottom-16 left-3 z-10 bg-[#0b0b0b]/90 text-[#e7d3a1] overline text-[0.55rem] px-2.5 py-1 flex items-center gap-1.5 pointer-events-none" data-testid={`card-color-preview-${product.id}`}>
            <span className="w-3 h-3 rounded-full border border-[#e7d3a1]/40" style={{ background: swatchFor(hoverColor || selectedColor) }} />
            {hoverColor || selectedColor}
          </span>
        )}
        <button onClick={openLightbox} type="button" aria-label="Enlarge image"
          data-testid={`card-enlarge-${product.id}`}
          className="absolute top-3 right-3 z-20 bg-[#0b0b0b]/80 hover:bg-[#0b0b0b] text-[#e7d3a1] p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Maximize2 size={15} />
        </button>
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickViewOpen(true); }} type="button" aria-label="Quick view"
          data-testid={`card-quickview-${product.id}`}
          className="absolute top-3 right-14 z-20 bg-[#0b0b0b]/80 hover:bg-[#0b0b0b] text-[#e7d3a1] p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Eye size={15} />
        </button>
        <button onClick={quickAdd}
          data-testid={`card-quick-add-${product.id}`}
          className="absolute bottom-0 left-0 right-0 z-10 bg-[#0b0b0b]/95 text-[#e7d3a1] overline text-[0.65rem] py-3.5 flex items-center justify-center gap-2 translate-y-full group-hover:translate-y-0 transition-transform duration-400">
          {product.external_url ? <><ExternalLink size={13} /> Buy From Website</> : <><Plus size={14} /> Quick Add</>}
        </button>
      </div>
      <div className="pt-4 text-center">
        <div className="flex items-center justify-center gap-1 mb-1.5">
          <Star size={12} className="fill-[#c6a15b] text-[#c6a15b]" />
          <span className="font-body text-xs text-[#6b6459]">{product.rating} · {product.reviews} reviews</span>
        </div>
        <h3 className="font-display text-lg text-[#1a1a1a] leading-snug px-2">{product.name}</h3>
        {sale ? (
          <p className="font-body mt-1 tracking-wide flex items-center justify-center gap-2" data-testid={`card-price-${product.id}`}>
            <span className="text-[#a39c8e] line-through text-sm">{product.price_by_length ? 'From ' : ''}${product.price.toFixed(2)}</span>
            <span className="text-emerald-600 font-semibold">${withSale(product.price, sale).toFixed(2)}</span>
          </p>
        ) : (
          <p className="font-body text-[#a8823c] mt-1 tracking-wide">{product.price_by_length ? 'From ' : ''}${product.price.toFixed(2)}</p>
        )}
        {colors.length > 0 && (
          <div className="flex items-center justify-center gap-1.5 mt-2.5" data-testid={`card-swatches-${product.id}`}>
            {colors.slice(0, showAllSwatches ? colors.length : maxSwatches).map((c) => {
              const preview = colorImages[c];
              if (!preview) {
                return (
                  <button key={c} title={c} type="button" data-testid={`card-swatch-${product.id}-${c}`}
                    onMouseEnter={() => setHoverColor(c)}
                    onMouseLeave={() => setHoverColor(null)}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedColor(selectedColor === c ? null : c); }}
                    className={`w-3.5 h-3.5 rounded-full border shrink-0 cursor-pointer transition-transform hover:scale-125 ${selectedColor === c || hoverColor === c ? 'ring-2 ring-[#c6a15b] ring-offset-1 border-[#c6a15b]' : 'border-black/15'}`}
                    style={{ background: swatchFor(c) }} />
                );
              }
              return (
                <button key={c} title={c} type="button" data-testid={`card-swatch-${product.id}-${c}`}
                  onMouseEnter={() => setHoverImg(preview)}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedColor(selectedColor === c ? null : c); setHoverImg(null); }}
                  className={`w-4 h-4 rounded-full border shrink-0 cursor-pointer transition-transform hover:scale-125 ${selectedColor === c || hoverImg === preview ? 'ring-2 ring-[#c6a15b] ring-offset-1 border-[#c6a15b]' : 'border-black/15'}`}
                  style={{ background: swatchFor(c) }} />
              );
            })}
            {colors.length > maxSwatches && (
              <button type="button" data-testid={`card-swatches-toggle-${product.id}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowAllSwatches((s) => !s); }}
                className="font-body text-[0.65rem] text-[#8a8375] hover:text-[#a8823c] underline underline-offset-2 transition-colors">
                {showAllSwatches ? 'less' : `+${colors.length - maxSwatches}`}
              </button>
            )}
          </div>
        )}
        {colors.length > 1 && Object.keys(colorImages).length > 0 && (
          <ShadeCompare
            colors={colors}
            variant="card"
            imageFor={(c) => product.color_galleries?.[c]?.[0] || colorImages[c]}
            lengths={product.lengths || []}
          />
        )}
      </div>
      <ImageLightbox images={enlargeImages} open={lightboxOpen} onClose={() => setLightboxOpen(false)} />
      <QuickView product={product} open={quickViewOpen} onClose={() => setQuickViewOpen(false)} />
    </Link>
    </Tilt>
  );
};

export default ProductCard;
